import { useEffect, useState } from "react";
import { columnTextField } from "utils/tables-utils";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import { Box, Button, Typography, Checkbox, Tooltip } from "@material-ui/core";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import Api from "lib/api/httpRequestHelper";
import UtilsFncs from "utils/UtilsFncs";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import ModalShowImage from "./modal-show-image";
import ModalHistoryExport from "./modal-history-export";
import {
  DEFAULT_IMAGE_URL,
  handleCatalogImageLoadError,
} from "./catalog-produto-image-default";

const ACCEPTED_FILE_TYPE = "image/jpeg";

export default function TableCatalogoProdutos({ data }) {
  const [dataTable, setDataTable] = useState(data);
  const [showingPhoto, setShowingPhoto] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [downloadList, setDownloadList] = useState(null);
  const [loading, setLoading] = useState({
    upload: false,
    delete: false,
    deleteConfirm: false,
    downloading: false,
  });
  const snackbar = useSnackbar();

  useEffect(() => {
    setDataTable(
      data.map((item) => ({
        ...item,
        selected: false,
      })),
    );
  }, [data]);

  const handleSelectRow = (code) => {
    setDataTable((prevData) =>
      prevData.map((item) =>
        item.code === code ? { ...item, selected: !item.selected } : item,
      ),
    );
  };

  const handleSelectAll = () => {
    setDataTable((prevData) =>
      prevData.map((item) => ({
        ...item,
        selected: !selectAll,
      })),
    );
    setSelectAll(!selectAll);
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (selectedFile.type !== ACCEPTED_FILE_TYPE) {
      snackbar.showMessage("Formato indisponível! Utilize apenas .jpg/.jpeg", {
        severity: "error",
      });
      return;
    }

    setLoading((prev) => ({ ...prev, upload: true }));

    try {
      const photoBase64 = await UtilsFncs.FileToB64(selectedFile);
      const encodedPhotoBase64 = photoBase64
        ?.toString()
        ?.replace(/^data:(.*,)?/, "");

      const response = await Api.put(
        "upload-product-image",
        "trocasDevolucoes",
        {
          idProdutoMidia: showingPhoto.id,
          arquivo: encodedPhotoBase64,
          nomeArquivo: "ean_" + String(showingPhoto.ean),
          extensao: "jpeg",
        },
        true,
      );

      const newPhotoData = {
        fileName: response.Result.Url.split("/").pop(),
        id: response.Result.IdProdutoMidia,
        productId: showingPhoto.productId,
        url: response.Result.Url,
      };

      setDataTable((oldState) =>
        oldState.map((item) => {
          if (item.code === showingPhoto?.productId) {
            return {
              ...item,
              photos: item.photos.map((photo) =>
                photo.id === showingPhoto?.id ? newPhotoData : photo,
              ),
            };
          }
          return item;
        }),
      );

      setShowingPhoto((prev) => {
        const merged = {
          ...prev,
          url: response.Result.Url,
          id: response.Result.IdProdutoMidia,
          fileName: newPhotoData.fileName,
        };
        return {
          ...merged,
          galleryPhotos: prev.galleryPhotos?.map((p) =>
            p.id === prev.id ? { ...p, ...merged } : p,
          ),
        };
      });
      snackbar.showMessage("Imagem adicionada com sucesso!");
    } catch (error) {
      console.log(error);
      snackbar.showMessage("Falha ao enviar imagem!", {
        severity: "error",
      });
    } finally {
      setLoading((prev) => ({ ...prev, upload: false }));
    }
  };

  const handleDeleteImage = async () => {
    setLoading((prev) => ({ ...prev, delete: true }));

    try {
      console.log(showingPhoto);

      await Api.delete(
        "delete-product-image",
        "trocasDevolucoes",
        {
          idProduto: showingPhoto.productId,
          idProdutoMidia: showingPhoto.id,
        },
        true,
      );

      const updatedPhoto = {
        ...showingPhoto,
        url: DEFAULT_IMAGE_URL,
      };

      setDataTable((oldState) =>
        oldState.map((item) => {
          if (item.code === showingPhoto?.productId) {
            return {
              ...item,
              photos: item.photos.map((photo) =>
                photo.id === showingPhoto?.id ? updatedPhoto : photo,
              ),
            };
          }
          return item;
        }),
      );

      setShowingPhoto((prev) => {
        const merged = { ...prev, url: DEFAULT_IMAGE_URL };
        return {
          ...merged,
          galleryPhotos: prev.galleryPhotos?.map((p) =>
            p.id === prev.id ? { ...p, ...merged } : p,
          ),
        };
      });
    } catch (error) {
      snackbar.showMessage("Falha ao deletar imagem!", { severity: "error" });
    } finally {
      setLoading((prev) => ({ ...prev, delete: false, deleteConfirm: false }));
    }
  };

  const handleDownloadSelectedImages = async () => {
    setLoading((prev) => ({ ...prev, downloading: true }));
    try {
      const selectedItems = dataTable.filter((item) => item.selected);

      if (!selectedItems.length) {
        snackbar.showMessage(
          "Selecione ao menos um produto para baixar as imagens.",
          { severity: "error" },
        );
        setLoading((prev) => ({ ...prev, downloading: false }));
        return;
      }

      await Api.post(
        "download-catalog-product",
        "trocasDevolucoes",
        {
          idProdutos: selectedItems.map((item) => item.code).join(","),
        },
        true,
      );

      handleGetZipLink();
    } catch {
      snackbar.showMessage("Falha ao baixar imagens!", { severity: "error" });
    }
  };

  const handleGetZipLink = async () => {
    try {
      const response = await Api.get(
        "list_download-catalog-product",
        "trocasDevolucoes",
        "",
        true,
      );
      setDownloadList(response?.Result?.ListaDownload);
    } catch (error) {
      snackbar.showMessage("Falha ao obter link de download!", {
        severity: "error",
      });
    } finally {
      setLoading((prev) => ({ ...prev, downloading: false }));
    }
  };

  const columnModel = [
    {
      dataField: "selected",
      text: "",
      align: "center",
      headerAlign: "center",
      formatter: (cell, row) => (
        <Checkbox
          checked={row.selected}
          onChange={() => handleSelectRow(row.code)}
          color="primary"
        />
      ),
      headerFormatter: () => (
        <Checkbox
          checked={selectAll}
          onChange={handleSelectAll}
          color="primary"
        />
      ),
    },
    columnTextField(
      {
        dataField: "code",
        text: "Cód. Produto ",
        align: "center",
      },
      false,
    ),
    {
      ...columnTextField({
        dataField: "description",
        text: "Descrição do produto",
        align: "center",
      }),
      formatter: (cell, row) => {
        const fullText = row.descriptionFull || cell || "";
        return (
          <Tooltip title={fullText} placement="top">
            <span
              style={{
                display: "inline-block",
                maxWidth: "100%",
              }}
            >
              {cell}
            </span>
          </Tooltip>
        );
      },
    },
    columnTextField(
      {
        dataField: "line",
        text: "Linha",
        align: "center",
      },
      false,
    ),
    columnTextField(
      {
        dataField: "collection",
        text: "Coleção",
        align: "center",
      },
      false,
    ),
    columnTextField(
      {
        dataField: "type",
        text: "Tipo de produto",
        align: "center",
      },
      false,
    ),
    columnTextField(
      {
        dataField: "photos",
        text: "Fotos",
        align: "center",
        formatter: (cell, row) => {
          const galleryPhotos = (row.photos || []).filter((p) => p?.url);
          const first = galleryPhotos[0];

          if (!first) return null;
          return (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                type="button"
                key={row.code}
                style={{
                  all: "unset",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={() =>
                  setShowingPhoto({
                    ...first,
                    galleryPhotos,
                  })
                }
              >
                <img
                  src={first.url}
                  alt="Foto do produto"
                  width={72}
                  height={72}
                  onError={handleCatalogImageLoadError}
                />
              </button>
            </div>
          );
        },
      },
      false,
    ),
  ];

  return (
    <>
      <ToolkitTable
        dataTable={dataTable}
        searched={Boolean(dataTable)}
        keyField="code"
        notFoundTitle="Sua pesquisa não encontrou produtos correspondentes."
        columnModel={columnModel}
        CustomHeader={() => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            className="pl-4"
            style={{ height: 60, padding: "0 24px" }}
          >
            <Typography variant="h2">Produtos</Typography>
            <div style={{ display: "flex", gap: "10px" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleDownloadSelectedImages()}
                style={{ width: "20vw" }}
                disabled={loading.downloading}
              >
                {loading.downloading ? (
                  "Baixando..."
                ) : (
                  <>
                    <CloudDownloadOutlinedIcon /> Baixar imagens
                  </>
                )}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleGetZipLink()}
                style={{ width: "10vw" }}
                disabled={loading.downloading}
              >
                Ver Links
              </Button>
            </div>
          </Box>
        )}
      />

      <ModalShowImage
        showingPhoto={showingPhoto}
        setShowingPhoto={setShowingPhoto}
        loading={loading}
        setLoading={setLoading}
        handleFileChange={handleFileChange}
        handleDeleteImage={handleDeleteImage}
      />

      <ModalHistoryExport
        open={Boolean(downloadList)}
        handleClose={() => setDownloadList(null)}
        data={downloadList}
        handleGetZipLink={handleGetZipLink}
      />
    </>
  );
}
